import { DefaultTextInputFilterCell } from '.';
import { gearIcon } from '@progress/kendo-svg-icons';
import { isColumnMenuFilterActive, isColumnMenuSortActive } from '@progress/kendo-react-grid';
import { ColumnMenu } from '../ColumnMenu';

/**
 * Get the cell properties for a column.
 * @param {Object} item - The column item.
 * @returns {Object} The cell properties for the column.
 */
const getColumnCellsProps = (item) => {
  if (item.filterable) {
    return {
      cells: {
        filterCell: item.filterCell || DefaultTextInputFilterCell,
        ...(item.cell && { data: item.cell })
      }
    };
  }
  if (item.cell) {
    return {
      cells: {
        data: item.cell,
      }
    };
  }
  return {};
};

/**
 * Checks if a column is active based on filter, sort, and group states.
 * @param {Object} field - The column field.
 * @param {Object} filter - The current filter state.
 * @param {Object} sort - The current sort state.
 * @param {Array} group - The current group state.
 * @return {boolean} True if the column is active, false otherwise.
 */
const isColumnActive = ({ field, filter, sort, group }) => {
  if(!field) return false;

  const filterActive = isColumnMenuFilterActive(field, filter);
  const sortActive = isColumnMenuSortActive(field, sort);
  const groupActive = Array.isArray(group) && group.some(g => g.field === field);
  
  return filterActive || sortActive || groupActive;
};

/**
 * Get the column properties for a Kendo Grid column.
 * @param {Object} params - The parameters for the column.
 * @param {Object} params.item - The column item.
 * @param {Object} params.filter - The current filter state.
 * @param {Object} params.sort - The current sort state.
 * @param {Array} params.group - The current group state.
 * @param {string} params.headerClassName - The class name for the header.
 * @param {boolean} params.sortable - Whether sorting is enabled.
 * @param {Array} params.data - The data array for the grid.
 * @param {boolean} params.showColumnChooser - Whether to use the column chooser.
 * @returns {Object} The column properties.
 */
const getColumnProps = ({
  item,
  filter,
  sort,
  group,
  headerClassName,
  sortable,
  data,
  showColumnChooser
}) => {
  // Determine if the column is active (filtered, sorted, or grouped)
  const activeStatus = isColumnActive({ field: (item && item.field) || undefined, filter, sort, group }) ? 'active' : '';
  return {
    filterable: item.filterable,
    ...(item.hidden && { hidden: item.hidden }),
    ...(typeof item.groupable !== 'undefined' && { groupable: item.groupable }), // Set groupable to false to fix a column, otherwise handle grouping at the grid level
    ...(item.sortable && { sortable: item.sortable }),
    ...(item.headerCell && { headerCell: item.headerCell }),
    ...(item.filter && { filter: item.filter }),
    ...getColumnCellsProps(item),
    ...(item.showColumnMenu && data.length && {
      columnMenu: (props) => (
        <ColumnMenu
          {...props}
          sortable={sortable}
          showColumnChooser={showColumnChooser ?? false}
          showCheckboxFilter={item.showCheckboxFilter ?? false}
          data={data}
        />
      ),
      menuIcon: gearIcon
    }),
    headerClassName: [
      headerClassName,
      item.headerClassName,
      activeStatus
    ].filter(Boolean).join(' ')
  };
};

export { getColumnCellsProps, getColumnProps, isColumnActive };